import React from 'react';
import { useAppSelector } from 'hooks';

import { ClickableIcon } from '~components';
import { faArrowRotateBack, faEyeSlash, faLink, faLinkSlash } from '~components/icon';
import { AuthTags } from '~constants/authTags';
import { selectUserTags } from '~main/selectors/login';

import {
  GocardlessMandate,
  NON_LINKABLE_MANDATE_TAGS,
} from '~direct-debits/models';

interface ComponentProps {
  row: GocardlessMandate;
  onLink?: (mandate: GocardlessMandate) => void;
  onIgnore?: (mandate: GocardlessMandate) => void;
  onRestore?: (mandate: GocardlessMandate) => void;
  onUnlink?: (mandate: GocardlessMandate) => void;
  isBusy?: boolean;
  isConnected?: boolean;
}

const DISCONNECTED_TITLE = 'GoCardless connection is not active';

const MandateActions: React.FC<ComponentProps> = ({
  row,
  onLink,
  onIgnore,
  onRestore,
  onUnlink,
  isBusy = false,
  isConnected = true,
}) => {
  const userTags = useAppSelector(selectUserTags);
  const canUnlock = userTags.includes(AuthTags.DIRECTDEBITS.UNLOCK);
  const isNonLinkable = NON_LINKABLE_MANDATE_TAGS.has(row.status.tag);

  if (row.is_ignored) {
    return (
      <ClickableIcon
        id={`dd-mandate-restore-${row.id}`}
        dataCy={`dd-mandate-restore-${row.id}`}
        icon={faArrowRotateBack}
        color="primary"
        toolTip="Restore"
        ariaLabel="Restore"
        disabled={isBusy}
        onClick={() => onRestore?.(row)}
      />
    );
  }

  if (row.is_linked) {
    if (!canUnlock) {
      return null;
    }
    return (
      <ClickableIcon
        id={`dd-mandate-unlink-${row.id}`}
        dataCy={`dd-mandate-unlink-${row.id}`}
        icon={faLinkSlash}
        color="danger"
        toolTip={isConnected ? 'Unlink' : DISCONNECTED_TITLE}
        ariaLabel="Unlink"
        showTooltipWhenDisabled
        disabled={isBusy || !isConnected}
        onClick={() => onUnlink?.(row)}
      />
    );
  }

  let linkTooltip = 'Link';
  if (!isConnected) linkTooltip = DISCONNECTED_TITLE;
  else if (isNonLinkable) linkTooltip = 'This mandate cannot be linked';

  return (
    <div className="d-flex gap-2 justify-content-end align-items-center">
      <ClickableIcon
        id={`dd-mandate-link-${row.id}`}
        dataCy={`dd-mandate-link-${row.id}`}
        icon={faLink}
        color="success"
        toolTip={linkTooltip}
        ariaLabel="Link"
        showTooltipWhenDisabled
        disabled={isBusy || isNonLinkable || !isConnected}
        onClick={() => onLink?.(row)}
      />
      <ClickableIcon
        id={`dd-mandate-ignore-${row.id}`}
        dataCy={`dd-mandate-ignore-${row.id}`}
        icon={faEyeSlash}
        color="secondary"
        toolTip={isConnected ? 'Ignore' : DISCONNECTED_TITLE}
        ariaLabel="Ignore"
        showTooltipWhenDisabled
        disabled={isBusy || !isConnected}
        onClick={() => onIgnore?.(row)}
      />
    </div>
  );
};

export default MandateActions;
